import api from '../../../services/api';
import { phoneDigits, isValidPhone } from '../../../utils/formatting/phoneNumber';

// The payment form's API surface for a student who has no account yet. Every method the
// form calls on `api` has a counterpart here with the same name and response shape, backed
// by the public /payments/guest/* endpoints, so the form needs no guest-specific logic.
//
//  getEmail()      the email from the enrollment step (the new-vs-existing branch point)
//  getEmailToken() proof the email was confirmed with an emailed code (the server requires it)
//  onEmailVerificationRequired()  called when the server says that proof is missing or expired
//  onAccountExists()  called when the email gained an account (409): the buyer signs in instead
//  getDetails()    { firstName, lastName, phone } as currently typed on the form
//  applySession()  signs the brand-new student in after payment is verified
//
// No account exists until Stripe confirms payment; the server then builds it from these
// details (see guestCheckoutService.materializePendingCheckout).
// '' when the typed details are good enough to start a payment, otherwise what to tell the user.
export const validateBuyerDetails = (details) => {
  if (!(details.firstName || '').trim() || !(details.lastName || '').trim()) {
    return 'Please enter your first and last name.';
  }
  if (!isValidPhone(details.phone)) return 'Please enter a valid 10-digit phone number.';
  return '';
};

// The form reads server error details from `error.data` (what the signed-in api attaches);
// the public request layer calls it `errorData`. Make guest errors look the same.
const withErrorData = (fn) => async (...args) => {
  try {
    return await fn(...args);
  } catch (error) {
    if (error && error.errorData && !error.data) error.data = error.errorData;
    throw error;
  }
};

// The purchase went through: the details GuestPayment kept for a retry are no longer needed.
export const clearSavedGuestDetails = () => {
  try {
    Object.keys(sessionStorage).filter((k) => k.startsWith('guestCheckoutDetails:')).forEach((k) => sessionStorage.removeItem(k));
  } catch { /* nothing saved */ }
};

export function createGuestPaymentApi({ getEmail, getEmailToken, getDetails, applySession, onEmailVerificationRequired, onAccountExists }) {
  // Proof of possession for the completion call; set when the PaymentIntent is created.
  let clientSecret = null;

  const buyer = () => {
    const details = getDetails();
    const problem = validateBuyerDetails(details);
    if (problem) throw new Error(problem); // backstop: the form checks before it gets here
    return {
      firstName: details.firstName.trim(),
      lastName: details.lastName.trim(),
      email: getEmail(),
      emailToken: getEmailToken(),
      phone: phoneDigits(details.phone)
    };
  };

  // The proof of a confirmed email lasts 30 minutes; if it has lapsed the student goes back to confirm again.
  const verifying = (fn) => async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (error?.errorData?.code === 'email_verification_required' && onEmailVerificationRequired) {
        onEmailVerificationRequired();
      }
      // The email gained an account meanwhile: send them to sign in, then straight back to payment.
      if (error?.errorData?.requiresLogin && onAccountExists) {
        onAccountExists();
      }
      throw error;
    }
  };

  // After Stripe confirms: the server re-verifies the payment, creates the account and
  // enrolls, and returns a one-time session; then load the same confirmation data a
  // signed-in student sees.
  const completeAndLoad = async ({ paymentIntentId }) => {
    const result = await api.guestCompleteCheckout(paymentIntentId, clientSecret);
    if (!result.token || !result.user) {
      // The email gained an account while paying (or the one-time sign-in was used): the
      // purchase is attached to that account, they just need to sign in.
      window.location.assign('/portal/login');
      return new Promise(() => {});
    }
    applySession(result);
    const data = await api.getPurchaseConfirmation(paymentIntentId);
    return { success: true, ...data };
  };

  const methods = {
    // Signed-out visitors can't get (or need) a CSRF token: the guest endpoints are exempt.
    fetchCSRFToken: async () => {},

    getPublicCourses: () => api.getGuestCatalog(),
    getCoursePricing: (id) => api.getCoursePricing(id), // already public
    getAdditionalProduct: (id) => api.getGuestProduct(id),

    // Same shape as /bulk-pricing/calculate, taken from the server-side quote.
    calculateBulkPricing: async (courseIds) => {
      const quote = await api.guestQuote(courseIds, null, false);
      if (!quote.bulkPricing) return { success: true, bulkPricing: null, message: 'No bulk pricing available' };
      return {
        success: true,
        bulkPricing: quote.bulkPricing.bulkPricing,
        pricing: quote.bulkPricing.pricing,
        courses: quote.courses
      };
    },

    // Same shape as /promo-codes/validate. Invalid codes throw with the server's message.
    validatePromoCode: async (code, courseIds = [], productIds = []) => {
      const quote = productIds.length > 0
        ? await api.guestProductQuote(productIds, code)
        : await api.guestQuote(courseIds, code, false);
      const promo = quote.promoCode;
      return {
        success: true,
        promoCode: {
          code: promo.code,
          discount_type: promo.discount_type,
          discount_value: promo.discount_value,
          description: null,
          success_message: null
        }
      };
    },

    createPaymentIntent: verifying(async (request) => {
      const response = await api.guestCreatePaymentIntent({
        courseIds: request.courseIds,
        ...buyer(),
        promoCode: request.promoCode?.code || null,
        // The form sends no bulk discount once the student removed it (or none applies).
        skipBulk: !request.promoCode && !request.bulkPricing
      });
      // $0 order: the account already exists, so sign the new student in like after a paid checkout.
      if (response.freeEnrollment && response.session?.token) {
        clearSavedGuestDetails();
        applySession(response.session);
      }
      clientSecret = response.clientSecret || null;
      return response;
    }),

    createProductPaymentIntent: verifying(async (request) => {
      const response = await api.guestCreateProductPaymentIntent({
        productIds: request.productIds,
        ...buyer(),
        promoCode: request.promoCode?.code || null
      });
      clientSecret = response.clientSecret || null;
      return response;
    }),

    confirmPayment: completeAndLoad,
    confirmProductPayment: completeAndLoad
  };

  return Object.fromEntries(
    Object.entries(methods).map(([name, fn]) => [name, withErrorData(fn)])
  );
}
